import { useState, useEffect } from 'react';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import { Form, SelectInput, TextInput, useDataProvider, useNotify, useTranslate } from 'react-admin';
import { EditOutlined } from '@mui/icons-material';
import FormInputsSeparator from '@/components/common/form-inputs-separator/form-inputs-separator';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import Box from '@mui/material/Box';
import { useNavigate, useParams } from 'react-router';
import Joi from 'joi';
import { phoneExtension } from '@/utils/phoneValidation';
import { compact, merge, set, nth } from 'lodash';
import { joiResolver } from '@hookform/resolvers/joi';
import useGetManagerProfile from '@/hooks/useGetManagerProfile';
import {
    BIG_ADMIN,
    BIG_AGENCY_MANAGER,
    BIG_AGENCY_OPERATOR,
    BIG_CLIENT_SITE_MANAGER,
    BIG_CLIENT_SITE_OPERATOR,
    BIG_MANAGER_TAG,
    BIG_OPERATOR,
    BIG_SUB_MANAGER_CLIENT_TAG,
    BIG_SUB_MANAGER_SITE_TAG,
    BIG_SUPER_ADMIN,
    MANAGER_TAG,
    PROFILES,
    SUB_MANAGER_TAG,
    USER_PROFILES,
    USERS,
} from '@/utils/CONST';
import { useUpdate } from 'react-admin';
import useGetUser from '@/hooks/useGetUser';
import { composeUri } from '@/utils/compose';

interface Props {
    select?: boolean;
}

const EditUser = ({ select = false }: Props) => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const [update, { isLoading: isLoadingUpdate }] = useUpdate();
    // const { currentUser, isLoading: isLoadingCurrentUser } = useGetCurrentUser();
    const notify = useNotify();
    const { userId } = useParams();
    const { user, isLoading: isLoadingUser } = useGetUser({ userId: userId ?? '' });
    const dataProvider = useDataProvider();
    // console.log(userId);
    const navigateBack = () => {
        navigate(-1);
    };

    const [selectedProfile, setSelectedProfile] = useState<string>();
    const { profile, isLoading: isLoadingProfile } = useGetManagerProfile({
        profileName: selectedProfile,
    });

    // Inicializar selectedProfile con el tag del usuario cuando se carga
    useEffect(() => {
        if (user?.tags && !selectedProfile) {
            const userTag = user.tags.find((v: string) => USER_PROFILES.includes(v));
            if (userTag) {
                setSelectedProfile(userTag);
            }
        }
    }, [user, selectedProfile]);

    const customJoi = Joi.extend(phoneExtension);
    const schema = customJoi
        .object(
            merge(
                {},
                set({}, 'firstName', Joi.string().required()),
                set({}, 'lastName', Joi.string().required()),
                select ? set({}, 'tags', Joi.string().required()) : {},
                // set(
                //     {},
                //     'email',
                //     Joi.string()
                //         .email({ tlds: { allow: false } })
                //         .required(),
                // ),
            ),
        )
        .messages({
            'any.required': translate('ra.validation.required'),
            'string.base': translate('ra.validation.required'),
            // 'string.email': translate('ra.validation.email'),
        });

    const handleSubmit = async (values: any) => {
        let user_tags = compact([
            ...([BIG_SUPER_ADMIN, BIG_ADMIN, BIG_OPERATOR].includes(values.tags)
                ? [MANAGER_TAG, BIG_MANAGER_TAG]
                : [SUB_MANAGER_TAG]),
            [BIG_AGENCY_MANAGER].includes(values.tags) ? BIG_SUB_MANAGER_CLIENT_TAG : undefined,
            [BIG_CLIENT_SITE_MANAGER, BIG_CLIENT_SITE_OPERATOR, BIG_AGENCY_OPERATOR].includes(values.tags)
                ? BIG_SUB_MANAGER_SITE_TAG
                : undefined,
            values.tags,
        ]);
        
        // Obtener el perfil usando values.tags como respaldo si profile?.id no está disponible
        let profileId = profile?.id;
        if (!profileId && values.tags) {
            try {
                const profileResponse = await dataProvider.getList(PROFILES, {
                    pagination: {
                        page: 1,
                        perPage: 1,
                    },
                    filter: {
                        internalName: values.tags,
                    },
                });
                const profileData = nth(profileResponse.data);
                profileId = profileData?.id;
            } catch (error) {
                // Si falla, continuamos con profileId como null/undefined
            }
        }
        
        delete values.tags;

        update(
            USERS,
            {
                id: composeUri(USERS, userId),
                data: {
                    ...values,
                    // username: values['email'],
                    // company: currentUser?.company,
                    tags: user_tags,
                    profiles: profileId ? [profileId] : [],
                },
            },
            {
                onSuccess: () => {
                    notify('ra.notification.updated', { type: 'success' });
                    navigateBack();
                },
                onError: ({ response }: any) => {
                    notify(`messages.${response.data['@type']}`);
                },
            },
        );
    };

    return (
        <DialogTemplate
            closeButtonDisabled={isLoadingUpdate}
            title={translate('main.navigation.edit_user')}
            icon={<EditOutlined />}
            open={true}
        >
            <Form
                defaultValues={{
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    // email: user?.email,
                    tags: user?.tags?.find((v: string) => USER_PROFILES.includes(v)),
                }}
                resolver={joiResolver(schema)}
                onSubmit={handleSubmit}
                reValidateMode={'onBlur'}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                    }}
                >
                    <FormInputsSeparator>
                        {[
                            { source: 'lastName', label: 'main.navigation.last_name' },
                            { source: 'firstName', label: 'main.navigation.name' },
                            // { source: 'email', label: 'main.navigation.email' },
                        ].map(({ source, label }, index) => (
                            <TextInput key={index} helperText={false} name={source} source={source} label={label} />
                        ))}
                        {select && (
                            <SelectInput
                                helperText={false}
                                label={'users.list.user_type'}
                                source={'tags'}
                                onChange={(e: any) => {
                                    setSelectedProfile(e.target.value);
                                }}
                                choices={USER_PROFILES.map((v: string) => ({
                                    id: v,
                                    name: `users.profiles.${v.toLowerCase()}`,
                                }))}
                            />
                        )}
                    </FormInputsSeparator>

                    <ButtonsBlock>
                        <Button onClick={navigateBack} disabled={isLoadingUpdate}>
                            {translate('ra.action.back')}
                        </Button>

                        <LoadingButton
                            loading={isLoadingUpdate || isLoadingProfile || isLoadingUser}
                            type={'submit'}
                            variant={'contained'}
                        >
                            {translate('ra.action.edit')}
                        </LoadingButton>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default EditUser;
